// Preview-mode handlers for Attribution / Journeys / Insights Kinds.
// Every handler dispatches on the active preview profile and returns the
// per-profile numbers from ./dataset. Response shapes here mirror the wire
// shapes returned by the real /v2/internal/insights/* endpoints; the InsightsPage
// consumers are unmodified.

import type {
  ChannelContributionResponse,
  JourneySankeyResponse,
  JourneySummary,
  TopJourneysResponse,
} from "@/api/customer-journeys";
import type {
  DateRangeRequest,
  InsightsOverviewAttributionModel,
  InsightsOverviewJourney,
  InsightsOverviewResponse,
  InsightsOverviewSourceAnalysisData,
  IntegrationStatsPullResponse,
  RecommendationCardsResponse,
  SourceAnalysis,
  SourcePerformanceResponse,
  TotalConversionsResponse,
  TouchBreakdownResponse,
} from "@/api/insights";

import {
  getAttributionDataset,
  getPharmaRecommendationCards,
  platformRollup,
  type AttributionDataset,
  type AttributionCampaign,
  type AttributionPlatformKey,
  type AttributionSourceStageMix,
} from "./dataset";
import { getPreviewProfile } from "../profiles";
import { PREVIEW_ENV_ID } from "../runtime";

const ATTRIBUTION_MODELS: readonly InsightsOverviewAttributionModel[] = [
  "first_touch",
  "last_touch",
  "linear",
  "u_shaped",
  "j_shaped",
];

const JOURNEY_TYPES: readonly InsightsOverviewJourney[] = [
  "all_journeys",
  "single_source_journeys",
  "multi_source_journeys",
];

const PLATFORM_META: Record<
  AttributionPlatformKey,
  { key: string; name: string; sourceLabel: string }
> = {
  google_ads: { key: "google_ads", name: "Google Ads", sourceLabel: "Google" },
  meta_ads: { key: "meta_ads", name: "Meta Ads", sourceLabel: "Meta" },
  the_trade_desk: {
    key: "the_trade_desk",
    name: "The Trade Desk",
    sourceLabel: "DSP",
  },
};

// Per-model conversion multipliers so switching attribution models produces
// visibly different numbers on the page. Values are conservative — the
// point is that filters "do something", not to compete with real model math.
const MODEL_MULTIPLIER: Record<InsightsOverviewAttributionModel, number> = {
  last_touch: 1.0,
  first_touch: 0.94,
  linear: 0.97,
  u_shaped: 0.99,
  j_shaped: 1.02,
};

// Journey-filter fractions. Multi-channel is the ticket's 68% pharma share;
// single-channel picks up the rest. All-journeys covers the entire total.
function journeyFraction(
  journey: InsightsOverviewJourney,
  pctMultiChannel: number,
): number {
  switch (journey) {
    case "all_journeys":
      return 1;
    case "multi_source_journeys":
      return pctMultiChannel / 100;
    case "single_source_journeys":
      return 1 - pctMultiChannel / 100;
  }
}

function scaleCount(
  base: number,
  model: InsightsOverviewAttributionModel,
  journey: InsightsOverviewJourney,
  pctMultiChannel: number,
): number {
  return Math.round(
    base * MODEL_MULTIPLIER[model] * journeyFraction(journey, pctMultiChannel),
  );
}

function stagePerformance(
  dataset: AttributionDataset,
  model: InsightsOverviewAttributionModel,
  journey: InsightsOverviewJourney,
): SourcePerformanceResponse {
  const split = (
    total: number,
    mix: AttributionSourceStageMix,
  ): { key: string; name: string; count: number }[] => {
    const scaled = scaleCount(
      total,
      model,
      journey,
      dataset.summary.pctMultiChannel,
    );
    const rows: { key: string; name: string; count: number }[] = [];
    if (mix.google > 0) {
      rows.push({
        key: PLATFORM_META.google_ads.key,
        name: PLATFORM_META.google_ads.name,
        count: Math.round((scaled * mix.google) / 100),
      });
    }
    if (mix.meta > 0) {
      rows.push({
        key: PLATFORM_META.meta_ads.key,
        name: PLATFORM_META.meta_ads.name,
        count: Math.round((scaled * mix.meta) / 100),
      });
    }
    if (mix.dsp > 0) {
      rows.push({
        key: PLATFORM_META.the_trade_desk.key,
        name: PLATFORM_META.the_trade_desk.name,
        count: Math.round((scaled * mix.dsp) / 100),
      });
    }
    return rows;
  };

  const impressions = split(
    dataset.stageTotals.impressions,
    dataset.stageSourceMix.impressions,
  );
  const landingPageViews = split(
    dataset.stageTotals.landingPageViews,
    dataset.stageSourceMix.landingPageViews,
  );
  const midFunnel = split(
    dataset.stageTotals.midFunnelConversions,
    dataset.stageSourceMix.midFunnelConversions,
  );
  const lowFunnel = split(
    dataset.stageTotals.lowFunnelConversions,
    dataset.stageSourceMix.lowFunnelConversions,
  );

  const lowTotal = lowFunnel.reduce((s, r) => s + r.count, 0);
  const clicksTotal = landingPageViews.reduce((s, r) => s + r.count, 0);

  return {
    impressions,
    clicks: landingPageViews,
    midFunnelConversions: midFunnel,
    bottomFunnelConversions: lowFunnel,
    conversionRate: clicksTotal === 0 ? 0 : (lowTotal / clicksTotal) * 100,
  };
}

function totalConversions(
  dataset: AttributionDataset,
  model: InsightsOverviewAttributionModel,
  journey: InsightsOverviewJourney,
): TotalConversionsResponse {
  const total = scaleCount(
    dataset.summary.totalConversions,
    model,
    journey,
    dataset.summary.pctMultiChannel,
  );
  const spend = dataset.summary.spendCents / 100;
  const clicksTotal = scaleCount(
    dataset.stageTotals.landingPageViews,
    model,
    journey,
    dataset.summary.pctMultiChannel,
  );
  const impressions = scaleCount(
    dataset.stageTotals.impressions,
    model,
    journey,
    dataset.summary.pctMultiChannel,
  );
  return {
    total,
    conversionRate: clicksTotal === 0 ? 0 : (total / clicksTotal) * 100,
    clicks: clicksTotal,
    impressions,
    spend,
    roas: dataset.summary.roasPct,
  };
}

function campaignNode(campaign: AttributionCampaign): SourceAnalysis {
  return {
    dimension: "campaign",
    key: campaign.key,
    label: campaign.label,
    type: "Paid",
    platformDataAvailable: true,
    clicks: 0,
    impressions: null,
    spend: campaign.spendCents / 100,
    numMidFunnelConversions: 0,
    numBottomFunnelConversions: campaign.conversions,
    customerAcquisitionCost: campaign.cpaCents / 100,
    roas: null,
  };
}

function sourceAnalysis(
  dataset: AttributionDataset,
  model: InsightsOverviewAttributionModel,
  journey: InsightsOverviewJourney,
): InsightsOverviewSourceAnalysisData {
  const scale = (n: number): number =>
    scaleCount(n, model, journey, dataset.summary.pctMultiChannel);

  const groups: { platform: AttributionPlatformKey; label: string }[] = [
    { platform: "google_ads", label: PLATFORM_META.google_ads.name },
    { platform: "meta_ads", label: PLATFORM_META.meta_ads.name },
    { platform: "the_trade_desk", label: PLATFORM_META.the_trade_desk.name },
  ];

  const nodes: SourceAnalysis[] = groups.map(({ platform, label }) => {
    const roll = platformRollup(dataset, platform);
    // DSP appears as impressions-only — mirrors theTradeDesk in the healthcare
    // dataset. No campaigns, no clicks, no spend, no CPA.
    if (platform === "the_trade_desk") {
      return {
        dimension: "source",
        key: PLATFORM_META.the_trade_desk.key,
        label,
        type: "Paid",
        platformDataAvailable: true,
        clicks: 0,
        impressions: scale(dataset.stageTotals.impressions),
        spend: null,
        numMidFunnelConversions: 0,
        numBottomFunnelConversions: 0,
        customerAcquisitionCost: null,
        roas: null,
      };
    }
    return {
      dimension: "source",
      key: PLATFORM_META[platform].key,
      label,
      type: "Paid",
      platformDataAvailable: true,
      clicks: 0,
      impressions: null,
      spend: roll.spendCents / 100,
      numMidFunnelConversions: 0,
      numBottomFunnelConversions: scale(roll.conversions),
      customerAcquisitionCost: roll.cpaCents / 100,
      roas: null,
      children: dataset.campaigns
        .filter((c) => c.platform === platform)
        .map((c) => ({
          ...campaignNode(c),
          numBottomFunnelConversions: scale(c.conversions),
        })),
    };
  });

  // Add a Direct/Unpaid rollup so single-source journeys reconcile with the
  // top-paths table. Numbers derived from the top-paths where the last touch
  // is Direct (145 + 48 + 42 for pharma → 235; scaled per model).
  const directLastTouch = dataset.topJourneys
    .filter((j) => j.channels[j.channels.length - 1] === "Direct")
    .reduce((s, j) => s + j.conversions, 0);
  nodes.push({
    dimension: "source",
    key: "direct",
    label: "Direct",
    type: "Unpaid",
    platformDataAvailable: true,
    clicks: 0,
    impressions: null,
    spend: null,
    numMidFunnelConversions: 0,
    numBottomFunnelConversions: scale(directLastTouch),
    customerAcquisitionCost: null,
    roas: null,
  });

  return { levels: ["source", "campaign"], nodes };
}

export function handleInsightsOverview(
  _params: DateRangeRequest,
): InsightsOverviewResponse {
  const dataset = getAttributionDataset(getPreviewProfile());
  const result = {} as InsightsOverviewResponse;
  for (const model of ATTRIBUTION_MODELS) {
    const perJourney = {
      source_analysis: {} as Record<
        InsightsOverviewJourney,
        InsightsOverviewSourceAnalysisData
      >,
      source_performance: {} as Record<
        InsightsOverviewJourney,
        SourcePerformanceResponse
      >,
      total_conversions: {} as Record<
        InsightsOverviewJourney,
        TotalConversionsResponse
      >,
    };
    for (const journey of JOURNEY_TYPES) {
      perJourney.source_analysis[journey] = sourceAnalysis(
        dataset,
        model,
        journey,
      );
      perJourney.source_performance[journey] = stagePerformance(
        dataset,
        model,
        journey,
      );
      perJourney.total_conversions[journey] = totalConversions(
        dataset,
        model,
        journey,
      );
    }
    result[model] = perJourney;
  }
  return result;
}

export function handleSourceTouchBreakdown(params: {
  sourceKey: string;
}): TouchBreakdownResponse {
  const dataset = getAttributionDataset(getPreviewProfile());
  const platform = (
    Object.keys(PLATFORM_META) as AttributionPlatformKey[]
  ).find((p) => PLATFORM_META[p].key === params.sourceKey);
  const label = platform
    ? PLATFORM_META[platform].name
    : params.sourceKey === "direct"
      ? "Direct"
      : params.sourceKey;
  const rollup =
    platform && platform !== "the_trade_desk"
      ? platformRollup(dataset, platform)
      : { conversions: 0, spendCents: 0, cpaCents: 0 };
  return {
    dimension: "source",
    key: params.sourceKey,
    label,
    metrics: [
      {
        type: "bottomFunnelConversions",
        currentTotal: rollup.conversions,
        timeSeries: [],
      },
    ],
  };
}

// -----------------------------------------------------------------------------
// Journeys
// -----------------------------------------------------------------------------

// Derives the channel-contribution table from the top-paths so both tables
// reconcile on the same screen. See the ticket's "Known inconsistency in
// the source PDF" section for why this is the authoritative approach.
export function handleChannelContribution(): ChannelContributionResponse {
  const dataset = getAttributionDataset(getPreviewProfile());
  const channels = new Set<string>();
  for (const j of dataset.topJourneys) {
    for (const c of j.channels) channels.add(c);
  }

  const rows = Array.from(channels)
    .filter((c) => c !== "Other")
    .map((channel) => {
      let first = 0;
      let last = 0;
      let assist = 0;
      let participated = 0;
      let positionSum = 0;
      let positionCount = 0;
      for (const j of dataset.topJourneys) {
        if (j.channels.includes(channel)) participated += j.conversions;
        if (j.channels[0] === channel) first += j.conversions;
        if (j.channels[j.channels.length - 1] === channel)
          last += j.conversions;
        // Assist = journeys where the channel appears but is neither first
        // nor last. Middle touches only.
        for (let i = 1; i < j.channels.length - 1; i++) {
          if (j.channels[i] === channel) assist += j.conversions;
        }
        for (let i = 0; i < j.channels.length; i++) {
          if (j.channels[i] === channel) {
            positionSum += (i + 1) * j.conversions;
            positionCount += j.conversions;
          }
        }
      }
      const total = first + last + assist;
      return {
        sourceName: channel,
        journeysParticipated: participated,
        avgPositionInJourney:
          positionCount === 0 ? 0 : positionSum / positionCount,
        totalAttributedCredit: total,
        firstTouchAttributed: first,
        lastTouchAttributed: last,
        assistAttributed: assist,
        assistRatePct: total === 0 ? 0 : (assist / total) * 100,
        avgDaysToConvert: dataset.summary.avgTimeToDays,
      };
    });
  return { channels: rows };
}

export function handleTopJourneys(): TopJourneysResponse {
  const dataset = getAttributionDataset(getPreviewProfile());
  const summary: JourneySummary = {
    totalConversions: dataset.summary.totalConversions,
    avgPathLength: dataset.summary.avgPathLength,
    avgTimeToDays: dataset.summary.avgTimeToDays,
    pctMultiChannel: dataset.summary.pctMultiChannel,
  };
  return {
    summary,
    journeys: dataset.topJourneys.map((j) => ({
      path: j.path,
      pathLength: j.channels.length,
      distinctChannels: new Set(j.channels).size,
      conversionCount: j.conversions,
      attributedConversions: j.conversions,
      avgTimeToDays: dataset.summary.avgTimeToDays,
    })),
  };
}

export function handleJourneySankey(): JourneySankeyResponse {
  const dataset = getAttributionDataset(getPreviewProfile());
  const nodes = new Map<
    string,
    { id: string; label: string; column: number }
  >();
  const links: {
    from: string;
    to: string;
    weight: number;
    attributedWeight: number;
  }[] = [];

  for (const j of dataset.topJourneys) {
    // Skip the "Other" catch-all: no channels to lay out.
    if (j.channels.length === 1 && j.channels[0] === "Other") continue;
    // Lowercase "conversion" matches the real /v2 endpoint's wire contract
    // (see back/insights/journey.go's buildSankeyResponse and the demo
    // dataset fixtures). ChannelJourneysView's useFilteredSankeyData does a
    // case-sensitive match on this label to find the terminal node; a
    // capitalized "Conversion" here silently drops every journey's terminal
    // node from the filtered result, leaving the sankey empty at every touch
    // count.
    const chain = [...j.channels, "conversion"];
    chain.forEach((label, i) => {
      const id = `${label}-${i}`;
      if (!nodes.has(id)) nodes.set(id, { id, label, column: i });
    });
    for (let i = 0; i < chain.length - 1; i++) {
      const from = `${chain[i]}-${i}`;
      const to = `${chain[i + 1]}-${i + 1}`;
      links.push({
        from,
        to,
        weight: j.conversions,
        attributedWeight: j.conversions,
      });
    }
  }

  return { nodes: Array.from(nodes.values()), links };
}

// -----------------------------------------------------------------------------
// Recommendation cards + integration status
// -----------------------------------------------------------------------------

export function handleRecommendationCards(): RecommendationCardsResponse {
  // The pharma profile ships 3 authored cards (see getPharmaRecommendationCards
  // in ./dataset) reflecting PHARMA_DATASET's real campaign/funnel numbers.
  // Healthcare still returns the empty-but-successful stub: the AI
  // recommendation surface wasn't in this ticket's scope for that profile,
  // and an empty "not_enough_recent_data" state keeps InsightsContext from
  // surfacing the recommendation-error toast.
  if (getPreviewProfile() === "pharma") {
    return {
      envId: PREVIEW_ENV_ID,
      state: "ok",
      cards: getPharmaRecommendationCards(),
      updatedAt: "2026-08-31T08:00:00Z",
    };
  }
  return {
    envId: PREVIEW_ENV_ID,
    state: "not_enough_recent_data",
    cards: [],
    updatedAt: new Date().toISOString(),
  };
}

export function handleIntegrationStatsPullStatus(): IntegrationStatsPullResponse {
  // Return UP_TO_DATE so InsightsContext treats every platform as freshly
  // pulled — no "connect your ad platform" or "error pulling data" UI
  // appears in preview mode.
  const now = new Date().toISOString();
  const oldest = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  return {
    newestDateAvailable: now,
    oldestDateAvailable: oldest,
    status: "UP_TO_DATE",
    whenPulled: now,
  };
}
